"use client";

import { ClipboardCheck, Compass, GitBranch, Repeat } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Process() {
  const steps = [{
    step: "STEP 01",
    icon: Compass,
    title: "Sales Initiation & Discovery",
    description: "A deep-dive alignment phase where we define your primary commercial objectives and map high-value market opportunities to ensure every subsequent tactic is rooted in your specific growth goals."
  }, {
    step: "STEP 02",
    icon: GitBranch,
    title: "Strategic Alignment & Modeling",
    description: "Sales-domain modeling against your specific industrial profile. Deliverables include high-fidelity territory plans, target account lists, and signed-off engagement roadmaps."
  }, {
    step: "STEP 03",
    icon: ClipboardCheck,
    title: "Phased Execution & Validation",
    description: "Parallel-run outreach campaigns with defined performance windows. Each phase is validated against conversion KPIs before scaling to the next market segment."
  }, {
    step: "STEP 04",
    icon: Repeat,
    title: "Continuous Growth & Quarterly Review",
    description: "Hand-off to our dedicated account management team with 24/7 reporting. Quarterly business reviews reconcile sales targets, market penetration, and roadmap against measured revenue outcomes."
  }];
  const heading = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="11673" id="the-sales-engine" className="bg-background text-foreground py-28 md:py-40">
      <div className="container mx-auto max-w-[1280px] px-6 md:px-12">
        {}
        <div ref={heading.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${heading.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <p className="font-mono text-[13px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            How We Engage
          </p>
          <div className="mt-6 h-px w-full bg-border" />
          <h2 className="mt-8 font-serif text-[clamp(2rem,3.6vw,3.25rem)] font-normal leading-[1.08] tracking-[-0.015em] text-foreground" style={{
          fontFamily: "var(--font-serif)"
        }}>
            {"A measured path from "}
            <em className="italic text-primary" style={{
            fontFamily: "var(--font-serif)"
          }}>
              discovery to revenue
            </em>
            {"."}
          </h2>
        </div>

        {}
        <div ref={grid.ref} className="relative mt-16 md:mt-20">
          {}
          <div className="pointer-events-none absolute left-0 right-0 top-1/2 hidden h-px -translate-y-1/2 origin-left bg-border md:block" style={{
          transform: grid.isVisible ? "translateY(-50%) scaleX(1)" : "translateY(-50%) scaleX(0)",
          transition: "transform 800ms cubic-bezier(0.22, 1, 0.36, 1) 500ms"
        }} />

          {}
          <div className="pointer-events-none absolute bottom-0 left-3 top-0 w-px origin-top bg-border md:hidden" style={{
          transform: grid.isVisible ? "scaleY(1)" : "scaleY(0)",
          transition: "transform 800ms cubic-bezier(0.22, 1, 0.36, 1) 500ms"
        }} />

          <div className="relative grid grid-cols-1 gap-6 md:grid-cols-4 md:gap-6">
            {steps.map((item, i) => {
            const Icon = item.icon;
            return <div data-index={i} key={i} className={`relative ml-8 md:ml-0 transition-all ease-[cubic-bezier(0.22,1,0.36,1)] ${grid.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
              transitionDuration: "600ms",
              transitionDelay: `${i * 100}ms`
            }}>
                  <div className="relative border border-border bg-card p-9">
                    <p className="font-mono text-[14px] font-medium tracking-[0.02em] text-primary">
                      {item.step}
                    </p>
                    <div className="mt-5">
                      <Icon className="h-6 w-6 text-foreground" strokeWidth={1.25} />
                    </div>
                    <h4 className="mt-5 font-default text-[clamp(1.0625rem,1.1vw,1.1875rem)] font-semibold leading-[1.35] tracking-[-0.005em] text-foreground">
                      {item.title}
                    </h4>
                    <p className="mt-3 font-default text-[15px] font-normal leading-[1.6] text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}